import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import chatService from '../services/chatService';
import knowledgeService from '../services/knowledgeService';
import vectorService from '../services/vectorService';
import { startSpeechRecognition } from '../utils/speechUtils';
import ttsService, { AVAILABLE_VOICES, DEFAULT_VOICE } from '../services/ttsService';
import './ChatInterface.css';

const TOPICS = [
  { id: 0, title: 'Free Talk', icon: '💬', primary: true, ready: true },
  { id: 1, title: 'Technology', icon: '🤖', ready: false },
  { id: 2, title: 'Work & Life', icon: '⚖️', ready: false },
  { id: 3, title: 'Travel', icon: '✈️', ready: false },
  { id: 4, title: 'Health', icon: '💪', ready: false },
  { id: 5, title: 'Education', icon: '📚', ready: false },
];

const AI_AVATAR = '🤖';

const ChatInterface = ({ username, sessionId, language, onBack }) => {
  const navigate = useNavigate();
  
  // 对话状态
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [showTopicSelection, setShowTopicSelection] = useState(true);
  
  // 批注关卡状态
  const [pendingMsgId, setPendingMsgId] = useState(null); // 正在等待用户决定的消息 ID
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [messageAnnotations, setMessageAnnotations] = useState({}); // { [msgId]: annotations[] }
  
  // 收集栏状态
  const [collectedWords, setCollectedWords] = useState([]);
  const [isSyncing, setIsSyncing] = useState(false);
  const [showKnowledgeSuccess, setShowKnowledgeSuccess] = useState(false);
  const [recentCollected, setRecentCollected] = useState([]);
  
  // 语音状态
  const [isRecording, setIsRecording] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [autoSpeak, setAutoSpeak] = useState(true); // 自动朗读 AI 回复
  const [selectedVoice, setSelectedVoice] = useState(() => {
    const saved = ttsService.getCurrentVoice();
    // 如果保存的是旧格式，转换为新格式
    if (saved && !AVAILABLE_VOICES[saved]) {
      return DEFAULT_VOICE;
    }
    return saved || DEFAULT_VOICE;
  });
  const [showVoiceSelector, setShowVoiceSelector] = useState(false);
  const [hiddenTexts, setHiddenTexts] = useState(new Set()); // 记录文本隐藏的消息ID
  
  // 引导提示框
  const [showGuide, setShowGuide] = useState(() => {
    return localStorage.getItem('talktastic_hide_guide') !== 'true';
  });

  const dismissGuide = (permanently) => {
    setShowGuide(false);
    if (permanently) {
      localStorage.setItem('talktastic_hide_guide', 'true');
    }
  };

  // Refs
  const chatContainerRef = useRef(null);
  const recognitionRef = useRef(null);
  const transcriptRef = useRef(''); // 累积语音识别结果

  // 自动滚动
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messages]);

  // 加载最近收集的表达
  useEffect(() => {
    const recent = JSON.parse(localStorage.getItem('talktastic_recent_collected') || '[]');
    setRecentCollected(recent.slice(0, 5));
  }, []);

  // 初始化音色
  useEffect(() => {
    ttsService.initVoices();
  }, []);

  // 选择话题
  const handleTopicSelect = (topic) => {
    if (!topic.ready) {
      alert('此话题暂未更新，敬请期待！');
      return;
    }
    setSelectedTopic(topic);
    setShowTopicSelection(false);
    chatService.resetConversation();
    
    // AI 问候语 - 全英文
    let greeting;
    if (topic.id === 0) {
      greeting = "Hi there! I'm your English practice partner. Feel free to talk about anything you'd like. What's on your mind today?";
    } else {
      greeting = `Great choice! Let's discuss "${topic.titleEn}". Share your thoughts, experiences, or ask me anything about this topic!`;
    }
    
    const welcomeMessage = {
      id: Date.now(),
      role: 'assistant',
      content: greeting,
      timestamp: new Date().toISOString(),
    };
    setMessages([welcomeMessage]);
  };

  // 发送消息 → 分析 → 出批注 → 等用户决定
  const handleSendMessage = useCallback(async () => {
    if (!inputText.trim() || isLoading || isAnalyzing || pendingMsgId) return;
    
    if (isRecording) {
      stopRecording();
    }
    
    const userText = inputText.trim();
    const msgId = Date.now();
    setInputText('');
    setIsAnalyzing(true);
    
    const userMessage = {
      id: msgId,
      role: 'user',
      content: userText,
      timestamp: new Date().toISOString(),
    };
    setMessages(prev => [...prev, userMessage]);
    
    try {
      const vectorResults = await vectorService.search(userText);
      const annotations = await chatService.analyzeExpression(userText, vectorResults);
      console.log('表达分析结果:', annotations);
      
      if (annotations && annotations.length > 0) {
        setMessageAnnotations(prev => ({ ...prev, [msgId]: annotations }));
        setPendingMsgId(msgId);
        setIsAnalyzing(false);
      } else {
        setIsAnalyzing(false);
        await sendMessageAndGetResponse(userText);
      }
    } catch (err) {
      console.error('Analysis failed:', err);
      setIsAnalyzing(false);
      await sendMessageAndGetResponse(userText);
    }
  }, [inputText, isLoading, isAnalyzing, isRecording, pendingMsgId]);
  
  // 用户点「继续」→ 用原话获取 AI 回复
  const handleContinue = async () => {
    if (!pendingMsgId) return;
    const msg = messages.find(m => m.id === pendingMsgId);
    setPendingMsgId(null);
    if (msg) {
      await sendMessageAndGetResponse(msg.content);
    }
  };
  
  // 用户点「重说」→ 输入框清空，原消息+批注保持可见作为参考
  const handleResay = () => {
    if (!pendingMsgId) return;
    setInputText('');
    setPendingMsgId(null);
  };

  // 获取 AI 回复（用户消息已在 handleSendMessage 中添加）
  const sendMessageAndGetResponse = async (text) => {
    setIsLoading(true);
    
    try {
      let response;
      if (chatService.isConfigured()) {
        response = await chatService.generateResponse(text, selectedTopic?.titleEn);
      } else {
        response = await chatService.simulateResponse(text);
      }
      
      const aiMessageId = Date.now() + 1;
      const aiMessage = {
        id: aiMessageId,
        role: 'assistant',
        content: response,
        timestamp: new Date().toISOString(),
      };
      
      setMessages(prev => [...prev, aiMessage]);
      
      // 自动朗读 AI 回复时，先隐藏文本
      if (autoSpeak && response) {
        setHiddenTexts(prev => new Set([...prev, aiMessageId]));
        speakMessage(response);
      }
    } catch (error) {
      console.error('Response failed:', error);
      const errorMessage = {
        id: Date.now() + 1,
        role: 'assistant',
        content: "Sorry, I'm having trouble responding. Could you try again?",
        timestamp: new Date().toISOString(),
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };


  // 点击词块 → 加入收集栏
  const handleWordClick = (word) => {
    const cleaned = word.replace(/[^a-zA-Z'-]/g, '').trim();
    if (!cleaned || cleaned.length < 2) return;
    setCollectedWords(prev => {
      if (prev.some(w => w.toLowerCase() === cleaned.toLowerCase())) return prev;
      return [...prev, cleaned];
    });
  };

  // 从收集栏移除
  const handleRemoveCollected = (word) => {
    setCollectedWords(prev => prev.filter(w => w !== word));
  };

  // 确定 → 批量同步到知识库
  const handleConfirmCollection = async () => {
    if (collectedWords.length === 0 || isSyncing) return;
    setIsSyncing(true);
    let successCount = 0;
    for (const word of collectedWords) {
      try {
        const result = await knowledgeService.addItem(word);
        if (result.success) successCount++;
      } catch (e) {
        console.error('Sync failed:', word, e);
      }
    }
    const synced = [...collectedWords];
    setCollectedWords([]);
    setIsSyncing(false);
    if (successCount > 0) {
      const newCollected = [
        ...synced.map(w => ({ content: w, time: new Date().toISOString() })),
        ...recentCollected,
      ].slice(0, 8);
      setRecentCollected(newCollected);
      localStorage.setItem('talktastic_recent_collected', JSON.stringify(newCollected));
      setShowKnowledgeSuccess(true);
    }
  };

  // 渲染可点击词块
  const renderClickableWords = (text) => {
    return text.split(/(\s+)/).map((segment, i) => {
      if (/^\s+$/.test(segment)) return <span key={i}>{segment}</span>;
      const isCollected = collectedWords.some(w => w.toLowerCase() === segment.replace(/[^a-zA-Z'-]/g, '').toLowerCase());
      return (
        <span
          key={i}
          className={`word-chip ${isCollected ? 'word-collected' : ''}`}
          onClick={() => handleWordClick(segment)}
        >
          {segment}
        </span>
      );
    });
  };

  // 结束对话
  const handleEndConversation = () => {
    // 停止语音
    stopRecording();
    if (window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
    
    if (messages.length > 1) {
      const conversationData = {
        date: new Date().toISOString().split('T')[0],
        round: Date.now(),
        messages: messages,
        topic: selectedTopic,
      };
      
      const history = JSON.parse(localStorage.getItem('talktastic_chat_history') || '{}');
      const today = conversationData.date;
      
      if (!history[today]) history[today] = [];
      history[today].push(conversationData);
      
      localStorage.setItem('talktastic_chat_history', JSON.stringify(history));
    }
    
    setMessages([]);
    setSelectedTopic(null);
    setShowTopicSelection(true);
    chatService.resetConversation();
  };

  // ========== 语音功能 ==========
  
  // 开始语音输入
  const startRecording = () => {
    if (isRecording || isLoading || isAnalyzing || pendingMsgId) return;
    
    transcriptRef.current = '';
    setInputText('');
    setIsRecording(true);
    
    recognitionRef.current = startSpeechRecognition(
      'en-US',
      (results) => {
        if (results && results.length > 0) {
          const text = results[0].transcript;
          setInputText(text);
          transcriptRef.current = text;
        }
      },
      () => {
        console.log('Recording ended, final transcript:', transcriptRef.current);
        setIsRecording(false);
        recognitionRef.current = null;
      },
      (error) => {
        console.error('Speech recognition error:', error);
        setIsRecording(false);
        recognitionRef.current = null;
        if (error === 'network') {
          alert('语音识别需要连接 Google 服务器，请检查网络。\n建议使用 Safari 浏览器（无需VPN）。');
        } else if (error === 'not-allowed') {
          alert('麦克风权限未授予，请在浏览器设置中允许麦克风访问。');
        } else if (error === 'not-supported') {
          alert('当前浏览器不支持语音识别，请使用 Safari 或 Chrome 浏览器。');
        }
      }
    );
  };

  // 停止语音输入
  const stopRecording = () => {
    if (!recognitionRef.current) return;
    setIsRecording(false);
    try {
      recognitionRef.current.stop();
    } catch (e) {
      console.log('Stop recording error:', e);
    }
  };

  // 切换录音状态
  const toggleRecording = () => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  // 朗读 AI 消息
  const speakMessage = (text) => {
    if (!text) return;
    
    setIsSpeaking(true);
    
    const control = ttsService.speak(text, { 
      voice: selectedVoice,
      onEnd: () => {
        console.log('TTS: Speech ended');
        setIsSpeaking(false);
      },
      onError: (e) => {
        console.error('TTS: Speech error', e);
        setIsSpeaking(false);
      }
    });
    
    // 备用监听（某些浏览器 onend 不触发）
    if (window.speechSynthesis) {
      const checkSpeaking = setInterval(() => {
        if (!window.speechSynthesis.speaking && !window.speechSynthesis.pending) {
          setIsSpeaking(false);
          clearInterval(checkSpeaking);
        }
      }, 500);
      
      // 超时保护（最多60秒）
      setTimeout(() => {
        clearInterval(checkSpeaking);
        setIsSpeaking(false);
      }, 60000);
    }
    
    return control;
  };

  // 切换音色
  const handleVoiceChange = (voiceKey) => {
    setSelectedVoice(voiceKey);
    ttsService.setVoice(voiceKey);
    setShowVoiceSelector(false);
    
    // 找最后一条 AI 消息来试听，没有则用默认文本
    const lastAiMessage = [...messages].reverse().find(m => m.role === 'assistant');
    const previewText = lastAiMessage 
      ? lastAiMessage.content.substring(0, 100) + (lastAiMessage.content.length > 100 ? '...' : '')
      : "Hello! This is how I sound.";
    
    ttsService.speak(previewText, { voice: voiceKey });
  };

  // 停止朗读
  const stopSpeaking = () => {
    ttsService.stopSpeaking();
    setIsSpeaking(false);
  };

  // 按键处理
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // 返回首页
  const handleBackToHome = () => {
    if (onBack) {
      onBack();
    } else {
      navigate('/');
    }
  };

  // ========== 话题选择页面 ==========
  if (showTopicSelection) {
    return (
      <div className="chat-interface">
        <button className="back-home-btn" onClick={handleBackToHome}>
          ← Back to Home
        </button>
        <div className="topic-selection">
          <h2>选择话题</h2>
          <p>选择一个你感兴趣的话题开始对话练习</p>
          
          <div className="chat-topic-grid">
            {TOPICS.map(topic => (
              <div 
                key={topic.id} 
                className={`chat-topic-card ${topic.primary ? 'primary' : ''} ${!topic.ready ? 'disabled' : ''}`}
                onClick={() => handleTopicSelect(topic)}
              >
                <span className="topic-icon">{topic.icon}</span>
                <span className="topic-title">{topic.title}</span>
                {!topic.ready && <span className="topic-badge">即将上线</span>}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ========== 主对话页面 ==========
  return (
    <div className="chat-interface">
      <button className="back-home-btn" onClick={handleBackToHome}>
        ← Back to Home
      </button>
      <div className="chat-main-container">
        {/* 左侧对话框 */}
        <div className="chat-left-panel">
          <div className="chat-header">
            <div className="ai-info">
              <span className="ai-avatar">{AI_AVATAR}</span>
              <span className="ai-name">AI Partner</span>
            </div>
            <button className="end-button" onClick={handleEndConversation}>
              End Chat
            </button>
          </div>

          {showGuide && (
            <div className="guide-overlay">
              <div className="guide-box">
                <button className="guide-close" onClick={() => dismissGuide(false)}>×</button>
                <h4>💡 标注说明</h4>
                <div className="guide-items">
                  <div className="guide-item">
                    <span className="guide-icon guide-red">A</span>
                    <span>红色标注 = 语法错误已修正</span>
                  </div>
                  <div className="guide-item">
                    <span className="guide-icon guide-purple">A</span>
                    <span>紫色标注 = 可替代的更好表达</span>
                  </div>
                  <div className="guide-item">
                    <span className="guide-icon">📚</span>
                    <span>带此图标 = 来自你的知识库</span>
                  </div>
                  <div className="guide-item">
                    <span className="guide-icon guide-chip">词</span>
                    <span>带边框的词 = 可点击收集到知识库</span>
                  </div>
                </div>
                <button className="guide-dismiss" onClick={() => dismissGuide(true)}>不再提示</button>
              </div>
            </div>
          )}

          <div className="chat-messages" ref={chatContainerRef}>
            {messages.map((message, msgIndex) => {
              const isTextHidden = hiddenTexts.has(message.id);
              const anno = messageAnnotations[message.id];
              const isClickable = msgIndex > 0 && msgIndex < messages.length - 1;
              
              return (
                <div key={message.id}>
                  <div className={`message ${message.role === 'user' ? 'user-message' : 'ai-message'}`}>
                    {message.role === 'assistant' && (
                      <span className="message-avatar">{AI_AVATAR}</span>
                    )}
                    <div className="message-content">
                      {message.role === 'assistant' && isTextHidden ? (
                        <>
                          <div className="audio-wave">
                            <span className="wave-bar"></span>
                            <span className="wave-bar"></span>
                            <span className="wave-bar"></span>
                            <span className="wave-bar"></span>
                            <span className="wave-bar"></span>
                          </div>
                          <button 
                            className="show-text-btn"
                            onClick={() => setHiddenTexts(prev => {
                              const newSet = new Set(prev);
                              newSet.delete(message.id);
                              return newSet;
                            })}
                            title="Show text"
                          >
                            👁️ Show Text
                          </button>
                        </>
                      ) : (
                        <>
                          {isClickable ? (
                            <span className="clickable-text">{renderClickableWords(message.content)}</span>
                          ) : (
                            message.content
                          )}
                          {message.role === 'assistant' && (
                            <button 
                              className="speak-btn"
                              onClick={() => speakMessage(message.content)}
                              title="Read aloud"
                            >
                              🔊
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  {/* 批注 */}
                  {message.role === 'user' && anno && (
                    <div className="inline-annotations">
                      {/* 语法：修正后的句子，语法修正标红，expression 词标紫（紫色优先） */}
                      {anno.filter(a => a.type === 'g' || a.type === 'grammar').length > 0 && (
                        <div className="anno-group anno-grammar">
                          <span className="anno-label">🔴 Grammar</span>
                          <div className="grammar-sentence">
                            {(() => {
                              const gAnnos = anno.filter(a => a.type === 'g' || a.type === 'grammar');
                              const eOriginals = new Set(
                                anno.filter(a => a.type === 'e' || a.type === 'expression')
                                  .map(a => (a.original || '').toLowerCase())
                              );
                              const text = message.content;
                              // 收集所有标记：grammar 替换 + expression 高亮
                              const marks = [];
                              for (const a of gAnnos) {
                                const orig = (a.original || '');
                                if (!orig) continue;
                                const idx = text.toLowerCase().indexOf(orig.toLowerCase());
                                if (idx >= 0) {
                                  // 检查修正词是否也是 expression 目标 → 紫色优先
                                  const isAlsoExpr = eOriginals.has(orig.toLowerCase());
                                  marks.push({ start: idx, end: idx + orig.length, correction: a.correction, color: isAlsoExpr ? 'purple' : 'red' });
                                }
                              }
                              // 在未被 grammar 覆盖的区域找 expression 词并标紫
                              anno.filter(a => a.type === 'e' || a.type === 'expression').forEach(a => {
                                const orig = (a.original || '');
                                if (!orig) return;
                                const idx = text.toLowerCase().indexOf(orig.toLowerCase());
                                if (idx >= 0 && !marks.some(m => idx >= m.start && idx < m.end)) {
                                  marks.push({ start: idx, end: idx + orig.length, text: orig, color: 'purple', isHighlight: true });
                                }
                              });
                              marks.sort((a, b) => a.start - b.start);
                              const filtered = [];
                              for (const m of marks) {
                                if (filtered.length === 0 || m.start >= filtered[filtered.length - 1].end) {
                                  filtered.push(m);
                                }
                              }
                              const parts = [];
                              let cursor = 0;
                              for (const m of filtered) {
                                if (m.start > cursor) {
                                  parts.push(<span key={`t${cursor}`}>{text.substring(cursor, m.start)}</span>);
                                }
                                if (m.isHighlight) {
                                  parts.push(<span key={`e${m.start}`} className="grammar-expr-word">{text.substring(m.start, m.end)}</span>);
                                } else {
                                  parts.push(<span key={`g${m.start}`} className={m.color === 'purple' ? 'grammar-expr-word' : 'grammar-fix'}>{m.correction}</span>);
                                }
                                cursor = m.end;
                              }
                              if (cursor < text.length) {
                                parts.push(<span key={`t${cursor}`}>{text.substring(cursor)}</span>);
                              }
                              return parts;
                            })()}
                          </div>
                        </div>
                      )}
                      {/* 表达优化 - 紫色，同词合并 */}
                      {anno.filter(a => a.type === 'e' || a.type === 'expression').length > 0 && (
                        <div className="anno-group anno-expression">
                          <span className="anno-label">✨ Better Expression</span>
                          {(() => {
                            const exprAnnos = anno.filter(a => a.type === 'e' || a.type === 'expression');
                            const grouped = {};
                            exprAnnos.forEach(a => {
                              const key = (a.original || '').toLowerCase();
                              if (!grouped[key]) grouped[key] = { original: a.original, items: [] };
                              grouped[key].items.push(a);
                            });
                            return Object.values(grouped).map((group, gi) => (
                              <div key={gi} className="expression-group">
                                {group.items.map((a, ai) => (
                                  <div key={ai} className="expression-row">
                                    {ai === 0 ? (
                                      <span className="expression-word">{group.original}</span>
                                    ) : (
                                      <span className="expression-word-placeholder"></span>
                                    )}
                                    <span className="expression-arrow"> → </span>
                                    <span className="expression-alt">
                                      <b>{a.correction}</b>
                                      {a.source === 'kb' && <span className="anno-kb-tag"> 📚</span>}
                                      {a.meaning && <span className="anno-meaning"> {a.meaning}</span>}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            ));
                          })()}
                        </div>
                      )}
                      {/* 关卡按钮 */}
                      {pendingMsgId === message.id && (
                        <div className="anno-actions">
                          <button className="anno-btn anno-resay" onClick={handleResay}>🔄 重说</button>
                          <button className="anno-btn anno-continue" onClick={handleContinue}>继续 →</button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            
            {isAnalyzing && (
              <div className="anno-loading-inline">✍️ Checking your expression...</div>
            )}
            {isLoading && (
              <div className="message ai-message">
                <span className="message-avatar">{AI_AVATAR}</span>
                <div className="message-content typing">
                  <span className="typing-dot"></span>
                  <span className="typing-dot"></span>
                  <span className="typing-dot"></span>
                </div>
              </div>
            )}
          </div>

          <div className="chat-input-area">
            <div className="voice-controls">
              <button 
                className={`voice-btn ${isRecording ? 'recording' : ''}`}
                onClick={toggleRecording}
                disabled={isLoading || isAnalyzing || !!pendingMsgId}
                title={isRecording ? '停止录音' : '语音输入'}
              >
                {isRecording ? '⏹️ 停止' : '🎤 语音'}
              </button>
              <button 
                className={`voice-btn ${isSpeaking ? 'speaking' : ''}`}
                onClick={isSpeaking ? stopSpeaking : () => {}}
                disabled={!isSpeaking}
                title="Stop speaking"
              >
                {isSpeaking ? '🔊 Speaking...' : '🔇'}
              </button>
              <div className="voice-selector-wrapper">
                <button 
                  className="voice-btn voice-select-btn"
                  onClick={() => setShowVoiceSelector(!showVoiceSelector)}
                  title="Select voice"
                >
                  🎭 {AVAILABLE_VOICES[selectedVoice]?.name || 'Voice'}
                </button>
                {showVoiceSelector && (
                  <div className="voice-selector-dropdown">
                    <div className="voice-selector-header">Select AI Voice</div>
                    {Object.entries(AVAILABLE_VOICES).map(([key, voice]) => (
                      <div 
                        key={key}
                        className={`voice-option ${selectedVoice === key ? 'selected' : ''}`}
                        onClick={() => handleVoiceChange(key)}
                      >
                        <div className="voice-option-name">
                          {voice.name} 
                          <span className="voice-locale">({voice.locale})</span>
                        </div>
                        <div className="voice-option-desc">{voice.description}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <label className="auto-speak-toggle">
                <input 
                  type="checkbox" 
                  checked={autoSpeak} 
                  onChange={(e) => setAutoSpeak(e.target.checked)}
                />
                Auto-play
              </label>
            </div>
            <div className="input-row">
              <textarea
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={isRecording ? '正在听...' : '输入文字或点击语音按钮...'}
                disabled={isLoading || isAnalyzing || !!pendingMsgId}
                autoComplete="off"
                autoCorrect="off"
                spellCheck="false"
              />
              <button 
                className="send-button" 
                onClick={handleSendMessage}
                disabled={!inputText.trim() || isLoading || isAnalyzing || !!pendingMsgId}
              >
                Send
              </button>
            </div>
          </div>
        </div>

        {/* 右侧收集栏 */}
        <div className="chat-right-panel">
          <div className="collection-basket">
            <div className="basket-header">
              <span className="basket-icon">🧺</span>
              <h3>Expression Basket</h3>
            </div>
            <div className="basket-content">
              <div className="basket-input-area">
                <div className="basket-collect-zone">
                  {collectedWords.length > 0 ? (
                    <div className="basket-words">
                      {collectedWords.map((word, i) => (
                        <span key={i} className="basket-word-tag">
                          {word}
                          <button className="basket-word-remove" onClick={() => handleRemoveCollected(word)}>×</button>
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span className="basket-placeholder">点击对话中的单词即可收集...</span>
                  )}
                </div>
                <div className="basket-actions">
                  <button
                    className="basket-add-btn"
                    onClick={handleConfirmCollection}
                    disabled={collectedWords.length === 0 || isSyncing}
                  >
                    {isSyncing ? '同步中...' : collectedWords.length > 0 ? `同步到知识库 (${collectedWords.length})` : '同步到知识库'}
                  </button>
                  {showKnowledgeSuccess && (
                    <span className="basket-kb-link" onClick={() => navigate('/knowledge')}>✓ 查看知识库 →</span>
                  )}
                </div>
              </div>

              <div className="basket-tip">
                💡 Tip: 点击对话中的单词收集到这里，点击按钮同步到知识库。
              </div>

              {recentCollected.length > 0 && (
                <div className="collected-items">
                  <h4>Recently Added:</h4>
                  <div className="collected-list">
                    {recentCollected.map((item, idx) => (
                      <div key={idx} className="collected-item">
                        {item.content}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default ChatInterface;
